import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { GtmEventAction, GtmLobbyName } from "@obg/gtm.types/adaptive";
import { TelemetryService, ignoreEmpty, ignoreNil } from "@obg/ngx.global";
import { VirtualPageView } from "@obg/ngx.global.evo";
import { Route, RouteAction, RouteSelector, RouteService } from "@obg/ngx.router";
import { Observable } from "rxjs";
import { filter, map, skip, switchMap, take, tap } from "rxjs/operators";

import { Customer } from "../../customer-management/customer-management.model";
import { CustomerManagementSelector } from "../../customer-management/customer-management.selector";
import { CORE_ROUTE } from "../../route";
import { CoreState } from "../../state";
import { ANONYMOUS_CUSTOMER_ID } from "../auth/auth-telemetry.const";
import { LoginStatus } from "../auth/auth-telemetry.model";
import { TrackingData } from "../tracking-area/tracking-area.model";
import { TrackingAreaService } from "../tracking-area/tracking-area.service";

@Injectable()
export class RouteTelemetryEffect {
	private readonly EXTERNAL_PARAMS = ["utm"];

	navigationStart$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.navigationStart),
				map(({ payload: { url } }) => url),
				tap((url) => this.telemetryService.track({ CurrentTargetUrl: url }))
			),
		{ dispatch: false }
	);

	firstNavigateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.resolveEnd),
			take(1),
			switchMap(({ payload: { current } }) =>
				this.store.select(CustomerManagementSelector.getState).pipe(
					filter((customer) => customer.hasOwnProperty("customer")),
					take(1),
					map((customer) => customer.customer),
					tap((customer: Customer | undefined) => {
						this.trackGoogleOptimizeActivate({
							GUID: customer ? customer.customerId : ANONYMOUS_CUSTOMER_ID,
							LoginStatus: customer ? LoginStatus.LoggedIn : LoginStatus.LoggedOut,
							HashedEmail: customer?.hashedEmail || "Missing",
						});

						this.trackVirtualPageView(current);
						this.trackPageOpen(current.name);
					})
				)
			),
			switchMap(() => this.removeExternalQueryParams$())
		)
	);

	navigateSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.resolveEnd),
				map(({ payload: { current } }) => current),
				skip(1),
				ignoreNil(),
				tap((route: Route) => {
					this.trackGoogleOptimizeActivate();
					this.trackVirtualPageView(route);
					this.trackPageOpen(route.name);
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private routeService: RouteService,
		private store: Store<CoreState>,
		private telemetryService: TelemetryService,
		private trackingAreaService: TrackingAreaService
	) {}

	private trackVirtualPageView(route: Route) {
		if (route.data.trackVirtualPageView === false) {
			return;
		}

		const trackingData = this.trackingAreaService.getTrackingData(route);
		const sanitizedUrl = this.getSanitizedUrl(route, trackingData);

		this.store
			.select(RouteSelector.getSubArea)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe((subArea) => {
				const trackingPayload = this.getTrackingPayload(route, trackingData, sanitizedUrl);
				const updatedTrackingPayload = this.updateTrackingPayloadWithSubArea(trackingPayload, subArea);
				if (updatedTrackingPayload.SubArea) {
					this.dispatchSubAreaData(updatedTrackingPayload.SubArea);
				}
				this.telemetryService.track(updatedTrackingPayload);
			});
	}

	private removeExternalQueryParams$(): Observable<Action> {
		return this.store.select(RouteSelector.getQueryParams).pipe(
			ignoreNil(),
			take(1),
			map((queryParams) =>
				Object.keys(queryParams).reduce(
					(acc, param) => (this.EXTERNAL_PARAMS.includes(param) ? { ...acc, [param]: null } : acc),
					{}
				)
			),
			ignoreEmpty(),
			map((paramsToRemove) => RouteAction.updateQueryParams(paramsToRemove))
		);
	}

	private getSanitizedUrl(route: Route, trackingData: TrackingData | undefined): string | undefined {
		if (trackingData && (trackingData.maskedParams || trackingData.maskedQueryParams)) {
			return this.routeService.generateUrl({
				routeName: route.name,
				params: trackingData.maskedParams || route.params,
				extras: {
					queryParams: trackingData.maskedQueryParams || route.queryParams,
				},
			});
		}
		return undefined;
	}

	private getTrackingPayload(
		route: Route,
		trackingData: TrackingData | undefined,
		sanitizedUrl: string | undefined
	): VirtualPageView {
		return {
			Lobby: trackingData ? !!trackingData.lobby : false,
			LobbyName: trackingData ? trackingData.lobbyName : GtmLobbyName.Generic,
			VirtualUrl: sanitizedUrl || route.externalUrl,
			VirtualTitle: route.name,
			event: GtmEventAction.VirtualPageView,
		};
	}

	private updateTrackingPayloadWithSubArea(trackingPayload: VirtualPageView, subArea?: string): VirtualPageView {
		const lobbyNames = new Set([
			"CASINO",
			"POKER",
			"JACKPOTS",
			"LIVE_CASINO",
			"VIRTUAL_SPORTS",
			"SPORTSBOOK",
			"LIVE_SPORTSBOOK",
		]);
		const updatedPayload = { ...trackingPayload };
		if (!lobbyNames.has(updatedPayload.LobbyName)) {
			return updatedPayload;
		}

		if (subArea) {
			updatedPayload.SubArea = subArea;
		}

		if (trackingPayload.Lobby) {
			updatedPayload.SubArea = "Product Lobby Page";
		}

		if (trackingPayload.VirtualTitle.endsWith(".categories")) {
			updatedPayload.SubArea = "Game Categories";
		}

		return updatedPayload;
	}

	private dispatchSubAreaData(subArea: string) {
		this.store.dispatch(RouteAction.setDataForSubArea({ subArea: subArea }));
	}

	private trackPageOpen(routeName: string) {
		if (routeName === CORE_ROUTE.home) {
			this.telemetryService.track({ event: "HomeVisits" });
		}
	}

	private trackGoogleOptimizeActivate(trackingData?: Record<string, any>) {
		this.telemetryService.track({
			event: "optimize.activate",
			...trackingData,
		});
	}
}
